import React from 'react';
import { useIntl } from 'react-intl';
import application from 'services/application';
import Checkbox from 'components/checkbox';
import type { InputElement } from 'components/input';
import Row, { type RowOnChange } from 'components/form/row';
import is from 'utils/is';
import Link from 'components/link';
import { link } from 'services/navigation';

import { testId } from '../../registration.settings';
import type { SignupFormField } from '../../contracts';

import messages from './agreement-checkbox.i18n';

interface AgreementCheckboxElement extends SignupFormField<boolean>, Pick<InputElement, 'variant'> {
  onChange: RowOnChange<boolean>;
}

const AgreementCheckbox: React.FunctionComponent<AgreementCheckboxElement> = (props) => {
  const { initialValue, error, rejectionReason, disabled, variant, onChange } = props;
  const { formatMessage } = useIntl();
  const [invalid, setInvalid] = React.useState<boolean>(initialValue);
  const hasIssue = error || invalid;
  const currentProduct = application.current;

  const handleOnChange = (checked: boolean): void => {
    if (is.func(onChange)) {
      onChange(checked, checked);
    }

    return setInvalid(!checked);
  };

  return (
    <Row
      hint={hasIssue && formatMessage(messages.pages.signup.registration.components.agreementCheckbox.hint)}
      testId={`${testId.agreementCheckbox}-row`}
      warning={rejectionReason || hasIssue}
    >
      <Checkbox onChange={handleOnChange} variant={variant} testId={testId.agreementCheckbox} disabled={disabled}>
        {formatMessage(messages.pages.signup.registration.components.agreementCheckbox[currentProduct].label, {
          a1: (value: React.ReactNode) => (
            <Link href={link.terms()} target="_blank" variant="primary">
              {value}
            </Link>
          ),
          a2: (value) => (
            <Link href={link.privacy()} target="_blank" variant="primary">
              {value}
            </Link>
          ),
          a3: (value) => (
            <Link href={link.cookies()} target="_blank" variant="primary">
              {value}
            </Link>
          ),
        })}
      </Checkbox>
    </Row>
  );
};

export type { AgreementCheckboxElement };
export default React.memo(AgreementCheckbox, (prev, next) => {
  return prev.initialValue === next.initialValue && prev.error === next.error && prev.disabled === next.disabled;
});
